<template>
  <Panel dark flat>
    <div class="inspections-panel">
      <div class="inspections-panel__header">
        <div class="inspections-panel__header-title typo-small-title">
          <CheckboardIcon />
          {{ $t('nav.inspections') }}
        </div>
        <router-link
          :to="{
            name: `${inspectionPath}s`,
            params: { scope: 'inspections' },
          }"
          class="inspections-panel__header-link"
        >
          {{ panelLinkText }}
          <RightArrowIcon class="icon icon--small" />
        </router-link>
      </div>
      <Loader v-if="loading" class="inspections-panel__loader" />
      <div v-if="!loading" class="inspections-panel__content">
        <div
          v-if="inspections && inspections.length === 0"
          class="inspections-panel__empty"
        >
          <p class="typo-ignore typo-small-title">
            <strong>{{ $t('operations.inspections_empty_title') }}</strong>
          </p>
          <p class="typo-ignore typo-small-title">
            {{ $t('operations.inspections_empty_message') }}
          </p>
        </div>
        <div v-if="inspections && inspections.length > 0">
          <div class="inspections-panel__sub-title typo-smallish">
            {{ $t('operations.recently_conducted') }}
          </div>
          <InspectionCard
            v-for="inspection in inspections"
            :key="inspection.id"
            :inspection="inspection"
            :pathName="inspectionPath"
          />
        </div>
        <router-link
          v-if="awaitingApprovalCount > 0"
          :to="{
            name: `${inspectionPath}s`,
            params: { scope: 'inspections' },
          }"
          class="inspections-panel__awaiting-approval"
        >
          {{ awaitingApprovalLabel }}
        </router-link>
      </div>
    </div>
  </Panel>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import useAjax from '@/functions/use_ajax';
import I18n from 'i18n-js';

import InspectionCard from '@/components/inspections/inspection_card.vue';
import Loader from '@/components/generic/loader.vue';
import Panel from '@/components/dashboard/panel.vue';

import CheckboardIcon from '@/images/checkboard.svg';
import RightArrowIcon from '@/images/arrow-right-16.svg';

const props = defineProps({
  inspectionPath: { type: String, required: true },
  inspectionsEndpoint: { type: String, required: true },
});

const ajax = useAjax();
const awaitingApprovalCount = ref(null);
const inspections = ref(null);
const loading = ref(false);

onMounted(() => {
  loadInspections();
});

watch(() => props.inspectionsEndpoint, loadInspections);

const panelLinkText = computed(() => {
  return inspections.value?.length > 0
    ? I18n.t('general.see_all')
    : I18n.t('general.view');
});

const awaitingApprovalLabel = computed(() => {
  if (awaitingApprovalCount.value === 1) {
    return I18n.t('operations.inspections_awaiting_approval_one');
  } else {
    return I18n.t('operations.inspections_awaiting_approval_plural', {
      count: awaitingApprovalCount.value,
    });
  }
});

async function loadInspections() {
  loading.value = true;

  let { ok, data } = await ajax(props.inspectionsEndpoint);

  if (ok) {
    inspections.value = data.inspections;
    awaitingApprovalCount.value = data.awaiting_approval_count;
  }

  loading.value = false;
}
</script>

<style lang="scss">
@import '@/stylesheets/global';

.inspections-panel {
  margin-bottom: bu(1.5);

  strong {
    font-weight: 500;
  }

  &__header {
    align-items: center;
    border-bottom: 1px solid map-get($background-color, default);
    display: flex;
    justify-content: space-between;
    padding: bu(1.25);
    padding: bu(1) bu(1.25);
    width: 100%;
  }

  &__header-title {
    align-items: center;
    display: flex;
    font-weight: 500;

    svg {
      fill: map-get($foreground-color, dark);
      margin-right: bu(1);
    }
  }

  &__header-link {
    align-items: center;
    color: map-get($foreground-color, dark);
    display: flex;
    justify-content: center;
  }

  &__content {
    padding: bu(1);
  }

  &__sub-title {
    color: map-get($foreground-color, dark);
    margin-bottom: bu(1);
    margin-left: bu(1);
  }

  &__empty {
    color: map-get($foreground-color, dark);
    padding: bu(2.5) bu(1.25);

    &:first-child p {
      margin-bottom: bu(1);
    }
  }

  &__awaiting-approval {
    align-items: center;
    background-color: transparentize(map-get($info-color, default), 0.8);
    border-radius: map-get($border-radius, heavy);
    color: map-get($info-color, tint);
    display: flex;
    margin-top: bu(0.5);
    padding: bu(1) bu(1.5);

    &::before {
      background-color: transparentize(map-get($info-color, tint), 0.8);
      border-radius: map-get($border-radius, circle);
      border: 1px solid map-get($info-color, tint);
      content: '';
      display: block;
      height: bu(1.5);
      margin-right: bu(2);
      width: bu(1.5);
    }
  }

  &__loader {
    min-height: rem(150);
  }
}
</style>
